import { createContext, useEffect } from "react";
import { useSelector } from "react-redux";
import i18n, { isRtlLanguage } from "@i18n";
import { RootState } from "@Reducers/index";

const LanguageContext = createContext({
  preferredLanguage: "en",
});

const isReactNative = typeof document === "undefined";

interface ProviderLanguageProps {
  children?: React.ReactNode;
}

/**
 * Applies the store's `preferredLanguage` to everything outside React: the i18next language, `<html dir>`
 * and the localStorage copy. The MUI/emotion RTL switch is not here — ProviderApp derives it from the same
 * store value during render, so both land in the same commit.
 */
function ProviderLanguage({ children }: Readonly<ProviderLanguageProps>) {
  const preferredLanguage = useSelector((state: RootState) => state.preferredLanguage);

  useEffect(() => {
    // Guarded like the localStorage write: this file also runs where `document` is undefined (see
    // `isReactNative`).
    if (!isReactNative) {
      document.documentElement.dir = isRtlLanguage(preferredLanguage) ? "rtl" : "ltr";
    }
    // Only when it differs: i18next emits `languageChanged` even for the language it already has, and that hands
    // every `useTranslation` consumer a new `t`. Every nested <ProviderApp> mounts its own copy of this component
    // (UserNameTagComponent wraps each avatar in one), so without the check each avatar that mounts relabels the
    // whole app, and `t`-keyed grid columns re-apply at a moment set by when that avatar's lazy chunk arrived (see
    // the ⚠️ KNOWN BUG at SlickGrid1's columns-effect skip). The first call still runs: init sets no language.
    // fire-and-forget: an effect cannot await; i18next re-renders subscribers when the bundle lands,
    // and the localStorage write below is independent of it.
    if (i18n.language !== preferredLanguage) void i18n.changeLanguage(preferredLanguage);
    if (!isReactNative) {
      globalThis.localStorage.setItem("preferredLanguage", preferredLanguage);
    }
  }, [preferredLanguage]);

  const Provider = LanguageContext.Provider;
  return (
    <Provider
      value={{
        preferredLanguage,
      }}
    >
      {children}
    </Provider>
  );
}

export default ProviderLanguage;
